// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as api from '../../api/client'
import { Pager, preferredPageSize, rememberPageSize } from '../../components/Pager'
import { friendlyError } from '../../api/errors'
import {
  ClinicalStatePill, PayerStatePill,
  ErrorBanner, LoadingState, EmptyState, relativeTime,
} from '../../components/workflow/StatePills'
import { usePharmacy } from './PharmacyLayout'
import { useRxRegistryFunnels, type RxFunnelColumn } from '../../components/common/RxRegistryFunnels'

// D-UI-2: each header names the registry column its cell shows. The registry decides what each
// one can do; the queue itself stays under the payer-block scope.
const FUNNEL_COLS: RxFunnelColumn[] = [
  { key: 'rx_number', label: 'RX #' },
  { key: 'patient_name', label: 'Patient' },
  { key: 'drug_name', label: 'Drug' },
  { key: 'payer_state', label: 'Payer issue' },
  { key: 'insurance_carrier', label: 'Carrier' },
  { key: 'state', label: 'Status' },
  { key: 'last_state_change_at', label: 'Last change' },
]

/**
 * Awaiting Insurance queue — Rxs blocked on payer adjudication.
 *
 * Unlike the other tabs (which filter on `state`), this tab filters on
 * `payer_state` because insurance issues can affect Rxs at any clinical
 * stage. A submitted Rx with a rejected payer state is still "awaiting
 * insurance" for the team that handles billing — they care about the
 * payer block, not the clinical step.
 *
 * P1.1 (e2e pagination): server-paged via the /rx payer_blocked filter. The old
 * client-side filter ran over the unpaginated /rx fetch, which hard-caps at the
 * newest 5,000 — on tenants past the cap (ASP: 9k+ Rx), payer blocks on the
 * older Rx NEVER appeared in this queue. The predicate (payer_state present and
 * not 'approved'/'not_required' — U-04: over-surface rather than miss a block)
 * now lives server-side, so the queue reaches every Rx and the pager total is
 * the true block count. This tab has no client filter/sort UI, so pure server
 * paging does not cross the round-table red line.
 */
export default function InsuranceTab() {
  const { refresh: refreshCounts } = usePharmacy()
  const navigate = useNavigate()

  const [pageRows, setPageRows] = useState<api.RxItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSizeRaw] = useState(() => preferredPageSize())
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  // Size change resets to page 1 in the same batched update (same rationale as StationQueue).
  const setPageSize = (n: number) => { rememberPageSize(n); setPage(1); setPageSizeRaw(n) }
  const fx = useRxRegistryFunnels(FUNNEL_COLS, { payerBlocked: true })
  // A new selection starts from the first page of the narrowed queue.
  useEffect(() => { setPage(1) }, [fx.sig])

  useEffect(() => {
    let dead = false
    setLoading(true); setError(null)
    api.listRxPaged({
      payerBlocked: true, page, pageSize,
      orderBy: 'last_state_change_at', orderDir: 'desc',
      filters: fx.filters,
    })
      .then(r => {
        if (dead) return
        setPageRows(r.items ?? [])
        setTotal(typeof r.total === 'number' ? r.total : (r.items?.length ?? 0))
        setTotalPages(r.total_pages ?? 1)
      })
      .catch(e => { if (!dead) setError(friendlyError(e)) })
      .finally(() => { if (!dead) setLoading(false) })
    return () => { dead = true }
    // fx.sig, not fx.filters: the array is a new object every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, fx.sig])
  useEffect(() => { refreshCounts() }, [refreshCounts])

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <h2 style={h2Style}>Awaiting insurance</h2>
        <p style={subtitleStyle}>Prescriptions blocked by payer adjudication, prior auth, or rejected claims.</p>
      </div>
      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
      <div style={cardStyle}>
        {/* The table stays mounted while a filtered page loads: swapping it for the spinner
            would unmount the open funnel after every tick. */}
        {loading && fx.filters.length === 0 && pageRows.length === 0 ? (
          <LoadingState label="Loading queue…" />
        ) : total === 0 && fx.filters.length === 0 ? (
          <EmptyState message="Nothing waiting on insurance right now." />
        ) : (
          <>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="claims" />
          {fx.chips && <div style={{ padding: '0 14px' }}>{fx.chips}</div>}
          <table style={tableStyle} {...fx.tableProps}>
            <thead>
              <tr>
                {FUNNEL_COLS.map(c => (
                  <th key={c.key} style={thStyle}>{c.label} {fx.funnel(c.key)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 && (
                <tr><td colSpan={FUNNEL_COLS.length} style={{ ...tdStyle, textAlign: 'center', color: 'var(--color-text-mute)' }}>
                  No claims match the filters.
                </td></tr>
              )}
              {pageRows.map(rx => (
                <tr key={rx.id} style={trStyle} onClick={() => navigate(`/rx/${rx.id}`)}>
                  <td style={tdStyle}>
                    <span style={mono}>{(rx as any).rx_number ?? '—'}</span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500 }}>{rx.patient_name}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500 }}>{rx.drug_name}</div>
                    <div style={mutedStyle}>{rx.drug_strength}</div>
                  </td>
                  <td style={tdStyle}><PayerStatePill state={rx.payer_state} /></td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 12 }}>{(rx as any).insurance_carrier ?? '—'}</span>
                  </td>
                  <td style={tdStyle}>
                    <ClinicalStatePill state={rx.state} />
                    {(rx as any).pioneerrx_rx_status && (
                      <div style={mutedStyle}>{(rx as any).pioneerrx_rx_status}</div>
                    )}
                  </td>
                  <td style={tdStyle}>{relativeTime(rx.last_state_change_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="claims" />
          </>
        )}
      </div>
    </>
  )
}

const h2Style: React.CSSProperties = { fontSize: 18, fontWeight: 600, margin: 0 }
const subtitleStyle: React.CSSProperties = { margin: '4px 0 0', color: 'var(--color-text-soft)', fontSize: 13 }
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8, overflow: 'hidden',
}
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px',
  background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: 'var(--color-text-mute)',
}
const trStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--color-border)', cursor: 'pointer',
}
const tdStyle: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'middle' }
const mutedStyle: React.CSSProperties = {
  fontSize: 11, color: 'var(--color-text-mute)', marginTop: 2,
}
const mono: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12,
}
